"use client";

import { ArrowDown, Compass } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function BattleHistory() {
  const milestones = [{
    marker: "P-06",
    title: "Roster Optimization",
    description: "A comprehensive performance audit establishes the baseline. Coaches, analysts, and sports psychologists evaluate each player's mechanical consistency.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_roster_audit.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_roster_audit.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_roster_audit.png-wvc-sizes",
    alt: "Coach reviewing player performance metrics on digital dashboard"
  }, {
    marker: "P-05",
    title: "Tactical Conditioning",
    description: "Five weeks of structured scrimmages. High-intensity reaction drills, map control simulations, and communication loops build the team's synergy.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_tactical_training.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_tactical_training.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_tactical_training.png-wvc-sizes",
    alt: "Esports team practicing in a high-tech gaming facility"
  }, {
    marker: "P-04",
    title: "Mechanical Skills Audit",
    description: "Aim training, movement optimization, ability cooldown management, and clutch scenarios. Each technique is rehearsed until it becomes muscle memory.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_mechanical_skills.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_mechanical_skills.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_mechanical_skills.png-wvc-sizes",
    alt: "Player focusing on high-speed mouse and keyboard inputs"
  }, {
    marker: "P-03",
    title: "Hardware Calibration",
    description: "Every peripheral is tuned, tested, and logged. From custom DPI settings to monitor refresh rates, nothing leaves the lab without scrutiny.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_hardware_lab.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_hardware_lab.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_hardware_lab.png-wvc-sizes",
    alt: "Gaming peripherals laid out on a clean tech workbench"
  }, {
    marker: "P-02",
    title: "Tournament Simulation",
    description: "High-pressure environment protocols replicate stage conditions. The team learns to thrive under the glare of the spotlight and crowd noise.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_stage_simulation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_stage_simulation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_stage_simulation.png-wvc-sizes",
    alt: "Players inside a simulated tournament stage environment"
  }, {
    marker: "P-01",
    title: "Final Briefing",
    description: "Draft strategies, ban phases, contingency protocols. The full squad convenes for the final tactical review before entering the arena.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_final_briefing.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_final_briefing.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/esports_final_briefing.png-wvc-sizes",
    alt: "Team reviewing strategy maps at a digital briefing table"
  }];
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.5
  });
  return <section data-section-id="3114" id="battle-history" className="bg-background text-foreground py-32 md:py-[200px] px-6 md:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 mb-24 md:mb-32">
          <div className={`lg:col-span-6 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase mb-8 text-primary">PREPARATION</p>
            <h2 className="font-default text-5xl md:text-6xl lg:text-[76px] leading-[1.05] tracking-tight text-foreground">
              Six weeks
              <br />
              <span className="italic font-light text-muted-foreground">
                before
              </span>{" "}
              the match.
            </h2>
          </div>
          <div className={`lg:col-span-5 lg:col-start-8 flex items-end transition-all duration-1000 delay-200 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
              Victory is secured long before the first round begins. At Pixel Force, preparation is a tactical discipline — six weeks of measured, deliberate training where every click, every callout, and every strategy compounds into the lethal readiness that the arena demands.
            </p>
          </div>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px md:-translate-x-1/2" style={{
          background: `linear-gradient(to bottom, transparent 0%, var(--primary) 8%, var(--primary) 92%, transparent 100%)`
        }} aria-hidden="true">
            <div className="absolute inset-0 animate-pulse" style={{
            background: `linear-gradient(to bottom, transparent, var(--primary), transparent)`,
            opacity: 0.4
          }} />
          </div>

          {}
          <div className="space-y-24 md:space-y-32">
            {milestones.map((milestone, i) => {
            const isOdd = i % 2 === 0;
            return <TimelineMilestone key={i} data-index={i} milestone={milestone} index={i} isLeft={isOdd} />;
          })}
          </div>

          {}
          <div ref={footerRef} className={`relative mt-24 md:mt-32 flex flex-col items-center transition-all duration-1000 ease-out ${footerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative z-10 flex items-center justify-center w-16 h-16 rounded-lg border border-primary shadow-lg" style={{
            backgroundColor: "var(--background)"
          }}>
              <Compass className="w-7 h-7 text-primary" strokeWidth={1.5} />
            </div>
            <div className="mt-6 flex items-center gap-3">
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-primary">
                To the Arena
              </p>
              <ArrowDown className="w-3.5 h-3.5 text-primary" strokeWidth={1.5} />
            </div>
          </div>
        </div>
      </div>
    </section>;
}
type Milestone = {
  marker: string;
  title: string;
  description: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
};
function TimelineMilestone({
  milestone,
  index,
  isLeft,
  ...rest
}: {
  milestone: Milestone;
  index: number;
  isLeft: boolean;
  "data-index"?: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  return <div ref={ref} data-index={rest["data-index"] ?? index} className="relative">
      {}
      <div className="absolute left-8 md:left-1/2 top-2 md:-translate-x-1/2 z-10" aria-hidden="true">
        <div className="relative">
          <div className={`w-3.5 h-3.5 rounded-lg transition-all duration-700 ${isVisible ? "scale-100" : "scale-50"}`} style={{
          backgroundColor: "var(--primary)",
          boxShadow: isVisible ? `0 0 0 4px hsla(180, 100%, 50%, 0.15), 0 0 20px hsla(180, 100%, 50%, 0.6)` : "none"
        }} />
          {isVisible && <div className="absolute inset-0 rounded-lg animate-ping" style={{
          backgroundColor: "var(--primary)",
          opacity: 0.3
        }} />}
        </div>
      </div>

      {}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-0">
        {}
        <div className={`pl-20 md:pl-0 md:pr-20 ${isLeft ? "md:col-start-1 md:text-right" : "md:col-start-2 md:hidden"}`}>
          {isLeft && <MilestoneContent milestone={milestone} isVisible={isVisible} alignRight={true} />}
        </div>

        {}
        <div className={`pl-20 md:pl-20 md:pr-0 ${!isLeft ? "md:col-start-2" : "md:col-start-2 md:hidden"}`}>
          {!isLeft && <MilestoneContent milestone={milestone} isVisible={isVisible} alignRight={false} />}
        </div>
      </div>
    </div>;
}
function MilestoneContent({
  milestone,
  isVisible,
  alignRight
}: {
  milestone: Milestone;
  isVisible: boolean;
  alignRight: boolean;
}) {
  return <div className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-x-0" : alignRight ? "opacity-0 -translate-x-8" : "opacity-0 translate-x-8"}`}>
      <p className="font-default italic text-3xl md:text-4xl mb-3 text-primary">
        {milestone.marker}
      </p>
      <h3 className="font-default text-2xl md:text-[32px] leading-tight mb-4 text-foreground">
        {milestone.title}
      </h3>
      <p className="text-base leading-relaxed mb-6 max-w-md text-muted-foreground" style={{
      marginLeft: alignRight ? "auto" : 0
    }}>
        {milestone.description}
      </p>
      <div className={`overflow-hidden rounded-lg ${alignRight ? "md:ml-auto" : ""}`} style={{
      width: "100%",
      maxWidth: "320px",
      aspectRatio: "4 / 3",
      border: `1px solid var(--border)`
    }}>
        <img src={milestone.image} data-wvc-srcset={milestone.srcset} data-wvc-sizes={milestone.sizes} alt={milestone.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
      </div>
    </div>;
}